import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Wrench, Loader2, Rocket } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { readPendingSelection, savePendingSelection, clearPendingSelection, TIER_META, displayPrice } from "@/lib/billing";
import { Footer } from "@/components/Footer";

export default function Login() {
  const navigate = useNavigate();
  const { login, register, user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [garageName, setGarageName] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState({ tier: null, period: "monthly" });

  useEffect(() => { if (!authLoading && user) navigate("/dashboard"); }, [user, authLoading, navigate]);

  useEffect(() => {
    const t = params.get("tier");
    const p = params.get("period") === "annual" ? "annual" : "monthly";
    if (t === "basic" || t === "pro" || t === "premium") {
      savePendingSelection(t, p);
      setPending({ tier: t, period: p });
    } else {
      setPending(readPendingSelection());
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === "login") await login(email.trim(), password);
      else await register(garageName.trim(), email.trim(), password, name.trim());

      const sel = readPendingSelection();
      if (sel.tier) {
        try {
          const res = await api("/billing/checkout", { method: "POST", body: JSON.stringify({ tier: sel.tier, billing_period: sel.period }) });
          clearPendingSelection();
          if (res.demo_mode) { navigate(`/billing/success?demo=1&tier=${sel.tier}&period=${sel.period}`); return; }
          window.location.assign(res.checkout_url);
          return;
        } catch { navigate("/billing"); return; }
      }
      navigate("/dashboard");
    } catch (e2) {
      setError(e2.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Hero side */}
      <div className="relative hidden flex-col justify-between bg-gradient-to-br from-midnight to-midnight-2 p-12 lg:flex">
        <Link to="/" className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-white/10"><Wrench className="h-5 w-5 text-brand" /></div>
          <div>
            <p className="font-display text-xl font-bold tracking-widest text-white">PRO GARAGES</p>
            <p className="text-xs text-white/60">Workshop OS for UK garages</p>
          </div>
        </Link>
        <div>
          <h2 className="font-display text-4xl font-bold leading-tight text-white">Run your entire garage from one screen.</h2>
          <p className="mt-4 max-w-md text-white/70">Jobs, quotes, MTD-compliant invoices, DVLA lookups and payments — everything in one place.</p>
        </div>
        <p className="text-xs text-white/40">© Pro Garages · Built for UK independent garages</p>
      </div>

      {/* Form side */}
      <div className="flex flex-col bg-background">
        <div className="flex flex-1 flex-col justify-center px-6 py-12 sm:px-16">
          <div className="mx-auto w-full max-w-md">
          <Link to="/" className="mb-8 flex items-center gap-2 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-midnight"><Wrench className="h-4 w-4 text-brand" /></div>
            <span className="font-display text-lg font-bold tracking-widest text-midnight">PRO GARAGES</span>
          </Link>

          <h1 className="font-display text-3xl font-bold uppercase text-midnight">{mode === "login" ? "Sign in to your workshop" : "Set up your garage"}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{mode === "login" ? "Welcome back." : "Get started in minutes."}</p>

          {pending.tier && (
            <div data-testid="pending-tier-banner" className="mt-5 flex items-center gap-3 rounded-lg border border-border bg-brand-tertiary p-3">
              <Rocket className="h-4 w-4 text-brand" />
              <div>
                <p className="text-[10px] font-extrabold tracking-widest text-brand-2">SELECTED PLAN</p>
                <p className="text-sm font-bold text-midnight">{TIER_META[pending.tier].name} · £{displayPrice(pending.tier, pending.period)}{pending.period === "annual" ? "/yr" : "/mo"} inc. VAT · 14-day free trial</p>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <>
                <Field testId="garage-name-input" label="GARAGE NAME" value={garageName} onChange={setGarageName} placeholder="Brixton Garage" />
                <Field testId="your-name-input" label="YOUR NAME" value={name} onChange={setName} placeholder="Alan Turner" />
              </>
            )}
            <Field testId="email-input" type="email" label="EMAIL" value={email} onChange={setEmail} placeholder="owner@garage.co.uk" />
            <Field testId="password-input" type="password" label="PASSWORD" value={password} onChange={setPassword} placeholder="••••••••" />

            {error && <p data-testid="login-error" className="rounded-md bg-danger-bg px-3 py-2 text-sm font-medium text-danger">{error}</p>}

            <button type="submit" disabled={loading} data-testid="submit-btn"
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3.5 text-sm font-extrabold tracking-widest text-white transition-opacity disabled:opacity-60">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : mode === "login" ? "SIGN IN" : "CREATE GARAGE"}
            </button>
          </form>

          {mode === "login" && (
            <Link to="/forgot-password" data-testid="forgot-password-link" className="mt-4 block text-center text-xs font-semibold text-muted-foreground underline">Forgot password?</Link>
          )}
          <button data-testid="switch-mode-btn" onClick={() => setMode(mode === "login" ? "register" : "login")} className="mt-4 block w-full text-center text-sm font-semibold text-brand-2">
            {mode === "login" ? "New garage? Register" : "Have an account? Sign in"}
          </button>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text", placeholder }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">{label}</span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoCapitalize="none"
        required
        className="w-full rounded-lg border border-input bg-card px-3.5 py-3 text-base text-midnight outline-none transition-colors focus:border-brand focus:ring-2 focus:ring-brand/20"
      />
    </label>
  );
}
